<script setup>
import { computed } from 'vue';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const referer = computed(() => {
  const pageVisits = props.conversationAttributes?.page_visits || [];
  if (pageVisits.length > 0) {
    return pageVisits[0].url;
  }
  return props.conversationAttributes.referer;
});
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

// Helper function to get category label from medium
const getCategoryLabel = medium => {
  if (!medium || medium === 'none') return null;

  const mediumLower = medium.toLowerCase();

  if (
    mediumLower === 'paid' ||
    mediumLower.includes('paid') ||
    mediumLower.includes('cpc') ||
    mediumLower.includes('ppc')
  ) {
    return 'Paid';
  }
  if (mediumLower === 'social' || mediumLower.includes('social')) {
    return 'Social Media';
  }
  if (
    mediumLower === 'organic' ||
    mediumLower.includes('organic') ||
    mediumLower.includes('seo')
  ) {
    return 'Organic';
  }
  if (
    mediumLower === 'email' ||
    mediumLower.includes('email') ||
    mediumLower.includes('mail')
  ) {
    return 'Email';
  }
  if (mediumLower === 'referral' || mediumLower.includes('referral')) {
    return 'Referral';
  }
  if (mediumLower === 'affiliate' || mediumLower.includes('affiliate')) {
    return 'Affiliate';
  }
  if (mediumLower === 'display' || mediumLower.includes('display')) {
    return 'Display';
  }

  return null;
};

// Helper function to check if source is a search engine
const isSearchEngine = source => {
  if (!source) return false;
  const sourceLower = source.toLowerCase();
  const searchEngines = [
    'google',
    'bing',
    'yahoo',
    'duckduckgo',
    'yandex',
    'baidu',
    'ask.com',
    'startpage',
    'ecosia',
    'brave search',
    'qwant',
    'seznam',
    'naver',
    'aol',
    'sogou',
  ];
  return searchEngines.some(engine => sourceLower.includes(engine));
};

// Helper function to check if source is social media
const isSocialMedia = source => {
  if (!source) return false;
  const sourceLower = source.toLowerCase();
  const socialPlatforms = [
    'instagram',
    'facebook',
    'twitter',
    'linkedin',
    'tiktok',
    'youtube',
    'pinterest',
    'snapchat',
    'whatsapp',
    'telegram',
    'discord',
    'reddit',
    'vkontakte',
    'wechat',
    'line',
    'weibo',
    'tumblr',
    'vimeo',
    'medium',
  ];
  return socialPlatforms.some(platform => sourceLower.includes(platform));
};

const sourceDisplay = computed(() => {
  const source = props.conversationAttributes.source;
  const medium = props.conversationAttributes.medium;
  const sourceDisplayValue = props.conversationAttributes.source_display;
  const utmSource = props.conversationAttributes.utm_source;
  const utmMedium = props.conversationAttributes.utm_medium;
  const campaign = props.conversationAttributes.campaign;

  // Use source_display if available (from backend)
  if (sourceDisplayValue) {
    // Try to extract category from the display value
    const effectiveMedium = medium || utmMedium;
    const effectiveSource = source || utmSource || sourceDisplayValue;
    const category = getCategoryLabel(effectiveMedium);

    // If we have a category, format it nicely
    if (category) {
      return `${category} • ${effectiveSource}`;
    }

    // Check if source itself indicates category
    if (isSearchEngine(effectiveSource)) {
      return `Organic • ${effectiveSource}`;
    }
    if (isSocialMedia(effectiveSource)) {
      return `Social Media • ${effectiveSource}`;
    }

    return sourceDisplayValue;
  }

  // Fallback: build from source components
  const sourceValue = source || utmSource;
  if (!sourceValue) return null;

  const effectiveMedium = medium || utmMedium;
  const category = getCategoryLabel(effectiveMedium);

  // Build display string
  let displayValue = sourceValue;

  // Add category prefix if available
  if (category) {
    displayValue = `${category} • ${sourceValue}`;
  } else if (isSearchEngine(sourceValue)) {
    // Infer category from source if medium not available
    displayValue = `Organic • ${sourceValue}`;
  } else if (isSocialMedia(sourceValue)) {
    displayValue = `Social Media • ${sourceValue}`;
  } else if (
    sourceValue.toLowerCase().includes('paid') ||
    sourceValue.toLowerCase().includes('ads') ||
    sourceValue.toLowerCase().includes('adwords')
  ) {
    displayValue = `Paid • ${sourceValue}`;
  } else if (sourceValue === 'Direct' || !sourceValue) {
    displayValue = 'Direct';
  }

  // Add campaign if present
  if (campaign) {
    displayValue += ` • ${campaign}`;
  }

  return displayValue;
});

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: sourceDisplay.value ? { value: sourceDisplay.value } : null,
      title: 'CONTACT_PANEL.SOURCE',
      key: 'static-source',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
      key: 'static-ip-address',
      type: 'static_attribute',
    },
  ].filter(attribute => {
    // Handle both content as value directly and content as object with value property
    if (!attribute.content) return false;
    if (typeof attribute.content === 'object' && attribute.content !== null) {
      return !!attribute.content.value;
    }
    if (attribute.content) {
      return true;
    }
    return false;
  })
);

// Helper to get value from content (handles both object and direct value)
const getContentValue = content => {
  if (!content) return '';
  if (typeof content === 'object' && content !== null) {
    return content.value || '';
  }
  return content;
};
</script>

<template>
  <div class="conversation--details">
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.key"
          :title="$t(element.title)"
          :value="getContentValue(element.content)"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="getContentValue(element.content)"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ getContentValue(element.content) }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
